import { ColumnDropDividerWrapper, ColumnDropWrapper, ColumnDropWrapperProps, DROP_ROOT, DndItemWithId, DndMode, DragWrapper, DragWrapperProps, useDndStore } from "designSystem/dnd/index";
import { observer } from "mobx-react-lite";
import React, { ReactElement, useEffect } from "react";

interface DragAndColumnDropWrapperProps<T extends DndItemWithId> extends Omit<DragWrapperProps<T>, "dragItem">, ColumnDropWrapperProps<T> {
	/** optional click away layer element */
	clickAwayLayer?: ReactElement;

	/** drag preview layer */
	dragPreviewLayer?: ReactElement;

	/** optional placeholder for divider */
	dropPlaceholder?: ReactElement;

	/** generate key for each item */
	generateKey?: (item: T) => string;

	/** list of items to enable dragging from and dropping on */
	list: T[];

	/** optional parent id for nested drop wrappers */
	parentId?: string;

	/** render item for each row */
	renderItem: (item: T) => JSX.Element;
}

export const DragAndColumnDropWrapper = observer(function DragAndColumnDropWrapper<T extends DndItemWithId>({
	canDrag = true,
	clickAwayLayer,
	disableBgColorChangeOnDrag,
	disableDragWhileLoading,
	disableDropZoneOutline,
	dragHandle,
	dragHandleStyle,
	dragPreviewLayer,
	dragType,
	dropTypes,
	forceLoadingState,
	generateKey,
	isFullScreen,
	isMultiSelect,
	isRootDropWrapper,
	list,
	mode = DndMode.Divider,
	onDrop,
	onEndDrag,
	parentId = DROP_ROOT,
	dropPlaceholder,
	renderItem,
	...otherProps
}: DragAndColumnDropWrapperProps<T>) {
	const { isDragging, isItemSelected, previewList, setList } = useDndStore();

	useEffect(() => {
		setList(list);
	}, [JSON.stringify(list.map((item) => item.id))]);

	function renderListItem(item: T, index: number): JSX.Element {
		const render = renderItem(item);

		return (
			<DragWrapper
				canDrag={canDrag}
				disableBgColorChangeOnDrag={disableBgColorChangeOnDrag}
				dragHandle={dragHandle}
				dragHandleStyle={dragHandleStyle}
				dragItem={item}
				dragType={dragType}
				isFullScreen={isFullScreen}
				isItemSelected={isItemSelected(item)}
				isMultiSelect={isMultiSelect}
				key={generateKey ? generateKey(item) : `csi-dragwrapper-${parentId}-${index}`}
				onEndDrag={onEndDrag}
			>
				{render}
			</DragWrapper>
		);
	}

	const renderList = (isDragging && mode === DndMode.Normal ? previewList : list).map(renderListItem);
	return (
		<ColumnDropWrapper
			disableDragWhileLoading={disableDragWhileLoading}
			disableDropZoneOutline={disableDropZoneOutline}
			dropTypes={dropTypes}
			forceLoadingState={forceLoadingState}
			isRootDropWrapper={isRootDropWrapper}
			mode={mode}
			onDrop={onDrop}
			{...otherProps}
		>
			<ColumnDropDividerWrapper
				dropPlaceholder={dropPlaceholder}
				mode={mode}
				parentId={parentId}
			>
				{renderList}
			</ColumnDropDividerWrapper>
			{clickAwayLayer}
			{dragPreviewLayer}
		</ColumnDropWrapper>
	);
});